import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';

interface OperatorPerformanceData {
    name: string;
    fullName: string;
    metresPerShift: number;
    metresPerBit: number;
}

interface DayNightData {
    shift: string;
    metresPerShift: number;
    totalMetres: number;
}

interface OperatorLeaderboards {
    bestBitLife: { operator: string; serial: string; metres: number }[];
    bestHammerLife: { operator: string; serial: string; metres: number }[];
    fastestPenRate: { operator: string; avgPenRate: number; holes: number }[];
    lowestRedrill: { operator: string; rate: number; total: number; redrilled: number }[];
}

interface OperatorPerformanceProps {
    operatorPerformance: OperatorPerformanceData[];
    dayNightData?: DayNightData[];
    leaderboards?: OperatorLeaderboards;
}

const tooltipStyle = {
    backgroundColor: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
};

export default function OperatorPerformance({
    operatorPerformance,
    dayNightData = [],
    leaderboards,
}: OperatorPerformanceProps) {
    const day = dayNightData.find((d) => d.shift === 'Day');
    const night = dayNightData.find((d) => d.shift === 'Night');
    const hasDayNight = day || night;

    return (
        <div className='space-y-6 mb-6'>
            {/* Operator Metres Chart */}
            <Card className='bg-card border-border'>
                <CardHeader>
                    <CardTitle className='text-foreground'>Operator Performance</CardTitle>
                </CardHeader>
                <CardContent>
                    {operatorPerformance.length > 0 ? (
                        <ResponsiveContainer width='100%' height={Math.max(350, operatorPerformance.length * 50)}>
                            <BarChart data={operatorPerformance} layout='vertical' margin={{ left: 10, right: 20 }}>
                                <CartesianGrid strokeDasharray='3 3' stroke='hsl(var(--border))' />
                                <XAxis type='number' stroke='hsl(var(--muted-foreground))' fontSize={12} unit='m' />
                                <YAxis dataKey='name' type='category' stroke='hsl(var(--muted-foreground))' fontSize={12} width={90} tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    formatter={(value, name) => [`${Number(value).toLocaleString()}m`, name === 'metresPerShift' ? 'Metres / Shift' : 'Metres / Bit']}
                                    labelFormatter={(label) => operatorPerformance.find((o) => o.name === label)?.fullName ?? label}
                                />
                                <Legend formatter={(value) => value === 'metresPerShift' ? 'Metres / Shift' : 'Metres / Bit'} />
                                <Bar dataKey='metresPerBit' fill='hsl(var(--chart-2))' radius={[0, 4, 4, 0]} barSize={14} />
                                <Bar dataKey='metresPerShift' fill='hsl(var(--primary))' radius={[0, 4, 4, 0]} barSize={14} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className='text-sm text-muted-foreground py-4'>No operator data for the selected period.</p>
                    )}
                </CardContent>
            </Card>

            {/* Operator Leaderboards */}
            {leaderboards && (
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                    {/* Best Bit Life */}
                    <Card className='bg-card border-border'>
                        <CardHeader>
                            <CardTitle className='text-foreground text-base flex items-center gap-2'>
                                <Trophy className='h-4 w-4' />
                                Best Drill Bit Life
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {leaderboards.bestBitLife.length > 0 ? (
                                <table className='w-full text-sm'>
                                    <thead>
                                        <tr className='border-b border-border text-left text-muted-foreground'>
                                            <th className='pb-1.5 font-medium w-8'>#</th>
                                            <th className='pb-1.5 font-medium'>Operator</th>
                                            <th className='pb-1.5 font-medium'>Bit Serial</th>
                                            <th className='pb-1.5 font-medium text-right'>Metres</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leaderboards.bestBitLife.map((r, i) => (
                                            <tr key={r.operator} className={cn('border-b border-border/30', i === 0 && 'bg-primary/5')}>
                                                <td className='py-1.5 text-muted-foreground'>{i + 1}</td>
                                                <td className='py-1.5 font-medium text-foreground'>{r.operator}</td>
                                                <td className='py-1.5 text-muted-foreground text-xs'>{r.serial}</td>
                                                <td className={cn('py-1.5 text-right font-medium', i === 0 && 'text-primary')}>{r.metres.toLocaleString()}m</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className='text-sm text-muted-foreground py-4'>No bit data for the selected period.</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Best Hammer Life */}
                    <Card className='bg-card border-border'>
                        <CardHeader>
                            <CardTitle className='text-foreground text-base flex items-center gap-2'>
                                <Trophy className='h-4 w-4' />
                                Best Hammer Life
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {leaderboards.bestHammerLife.length > 0 ? (
                                <table className='w-full text-sm'>
                                    <thead>
                                        <tr className='border-b border-border text-left text-muted-foreground'>
                                            <th className='pb-1.5 font-medium w-8'>#</th>
                                            <th className='pb-1.5 font-medium'>Operator</th>
                                            <th className='pb-1.5 font-medium'>Hammer Serial</th>
                                            <th className='pb-1.5 font-medium text-right'>Metres</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leaderboards.bestHammerLife.map((r, i) => (
                                            <tr key={r.operator} className={cn('border-b border-border/30', i === 0 && 'bg-primary/5')}>
                                                <td className='py-1.5 text-muted-foreground'>{i + 1}</td>
                                                <td className='py-1.5 font-medium text-foreground'>{r.operator}</td>
                                                <td className='py-1.5 text-muted-foreground text-xs'>{r.serial}</td>
                                                <td className={cn('py-1.5 text-right font-medium', i === 0 && 'text-primary')}>{r.metres.toLocaleString()}m</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className='text-sm text-muted-foreground py-4'>No hammer data for the selected period.</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Fastest Avg Penetration Rate */}
                    <Card className='bg-card border-border'>
                        <CardHeader>
                            <CardTitle className='text-foreground text-base flex items-center gap-2'>
                                <Trophy className='h-4 w-4' />
                                Fastest Avg Penetration Rate
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {leaderboards.fastestPenRate.length > 0 ? (
                                <table className='w-full text-sm'>
                                    <thead>
                                        <tr className='border-b border-border text-left text-muted-foreground'>
                                            <th className='pb-1.5 font-medium w-8'>#</th>
                                            <th className='pb-1.5 font-medium'>Operator</th>
                                            <th className='pb-1.5 font-medium text-right'>Avg m/hr</th>
                                            <th className='pb-1.5 font-medium text-right'>Holes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leaderboards.fastestPenRate.map((r, i) => (
                                            <tr key={r.operator} className={cn('border-b border-border/30', i === 0 && 'bg-primary/5')}>
                                                <td className='py-1.5 text-muted-foreground'>{i + 1}</td>
                                                <td className='py-1.5 font-medium text-foreground'>{r.operator}</td>
                                                <td className={cn('py-1.5 text-right font-medium', i === 0 && 'text-primary')}>{r.avgPenRate.toFixed(1)}</td>
                                                <td className='py-1.5 text-right text-muted-foreground'>{r.holes}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className='text-sm text-muted-foreground py-4'>No drilling-time data for the selected period.</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Lowest Redrill Rate */}
                    <Card className='bg-card border-border'>
                        <CardHeader>
                            <CardTitle className='text-foreground text-base flex items-center gap-2'>
                                <Trophy className='h-4 w-4' />
                                Lowest Redrill Rate
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {leaderboards.lowestRedrill.length > 0 ? (
                                <table className='w-full text-sm'>
                                    <thead>
                                        <tr className='border-b border-border text-left text-muted-foreground'>
                                            <th className='pb-1.5 font-medium w-8'>#</th>
                                            <th className='pb-1.5 font-medium'>Operator</th>
                                            <th className='pb-1.5 font-medium text-right'>Rate</th>
                                            <th className='pb-1.5 font-medium text-right'>Holes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leaderboards.lowestRedrill.map((r, i) => (
                                            <tr key={r.operator} className={cn('border-b border-border/30', i === 0 && 'bg-primary/5')}>
                                                <td className='py-1.5 text-muted-foreground'>{i + 1}</td>
                                                <td className='py-1.5 font-medium text-foreground'>{r.operator}</td>
                                                <td className={cn('py-1.5 text-right font-medium', i === 0 && 'text-primary')}>{r.rate}%</td>
                                                <td className='py-1.5 text-right text-muted-foreground'>{r.total} ({r.redrilled} redrills)</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className='text-sm text-muted-foreground py-4'>Not enough hole data for the selected period.</p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Day vs Night comparison */}
            {hasDayNight && (
                <Card className='bg-card border-border'>
                    <CardHeader>
                        <CardTitle className='text-foreground'>Day vs Night Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='border-b border-border text-left text-muted-foreground'>
                                        <th className='pb-2 font-medium'>Metric</th>
                                        <th className='pb-2 font-medium text-right'>Day</th>
                                        <th className='pb-2 font-medium text-right'>Night</th>
                                        <th className='pb-2 font-medium text-right'>Difference</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className='border-b border-border/50'>
                                        <td className='py-2.5 text-foreground font-medium'>Avg Metres / Shift</td>
                                        <td className='py-2.5 text-right text-foreground'>{day?.metresPerShift.toLocaleString() ?? '—'}m</td>
                                        <td className='py-2.5 text-right text-foreground'>{night?.metresPerShift.toLocaleString() ?? '—'}m</td>
                                        <td className='py-2.5 text-right'>
                                            {day && night ? (
                                                <span className={day.metresPerShift >= night.metresPerShift ? 'text-green-500' : 'text-red-500'}>
                                                    {day.metresPerShift >= night.metresPerShift ? '+' : ''}{(day.metresPerShift - night.metresPerShift).toLocaleString()}m
                                                </span>
                                            ) : '—'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className='py-2.5 text-foreground font-medium'>Total Metres</td>
                                        <td className='py-2.5 text-right text-foreground'>{day?.totalMetres.toLocaleString() ?? '—'}m</td>
                                        <td className='py-2.5 text-right text-foreground'>{night?.totalMetres.toLocaleString() ?? '—'}m</td>
                                        <td className='py-2.5 text-right'>
                                            {day && night ? (
                                                <span className={day.totalMetres >= night.totalMetres ? 'text-green-500' : 'text-red-500'}>
                                                    {day.totalMetres >= night.totalMetres ? '+' : ''}{(day.totalMetres - night.totalMetres).toLocaleString()}m
                                                </span>
                                            ) : '—'}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
